import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UploadCloud, Link2, Loader2, Image as ImageIcon } from 'lucide-react';
import { LibraryTile, LibraryFilters, filterImages } from './ImageLibraryPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX = 10 * 1024 * 1024;

const DropZone = ({ onFiles, busy }) => {
  const [over, setOver] = useState(false);
  const input = useRef(null);
  const drop = (e) => { e.preventDefault(); setOver(false); onFiles([...e.dataTransfer.files]); };
  return (
    <div data-testid="images-dropzone" onClick={() => !busy && input.current.click()}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}
      className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${over ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-[#E6DAC5] bg-white hover:border-[#3B164C]/40'}`}>
      {busy ? <Loader2 size={36} className="mx-auto text-[#3B164C] animate-spin mb-3" /> : <UploadCloud size={36} className="mx-auto text-[#C5A059] mb-3" />}
      <p className="font-medium text-[#1E1622]">{busy ? 'Uploading…' : 'Drag images here or click to upload'}</p>
      <p className="text-xs text-[#1E1622]/50 mt-1">JPG, PNG or WebP, up to 10 MB each. Large images are automatically resized for fast loading.</p>
      <input ref={input} type="file" accept="image/*" multiple hidden data-testid="images-upload-input" onChange={(e) => { onFiles([...e.target.files]); e.target.value = ''; }} />
    </div>
  );
};

export const ImagesTab = ({ token, onAddLink, refreshKey }) => {
  const [images, setImages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [usage, setUsage] = useState('all');
  const [source, setSource] = useState('all');
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => axios.get(`${API}/admin/image-library`, headers).then((r) => setImages(r.data.images)).catch(() => setImages([]));
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    const ok = files.filter((f) => f.type.startsWith('image/') && f.size <= MAX);
    if (ok.length < files.length) toast.error('Some files were skipped (images only, up to 10 MB)');
    if (!ok.length) return;
    setBusy(true);
    let done = 0; let saved = 0;
    for (const f of ok) {
      const form = new FormData(); form.append('file', f);
      try { const r = await axios.post(`${API}/admin/upload-image`, form, headers); done += 1; saved += Math.max(0, (r.data.original_size || 0) - (r.data.size || 0)); } catch { toast.error(`Upload failed: ${f.name}`); }
    }
    setBusy(false);
    if (done) toast.success(`${done} image${done > 1 ? 's' : ''} uploaded${saved > 102400 ? ` and optimised (saved ${(saved / 1048576).toFixed(1)} MB)` : ''}`);
    load();
  };

  const remove = async (img) => {
    if (!window.confirm(`Delete "${img.name}"?`)) return;
    try { await axios.delete(`${API}/admin/image-library/${img.id}`, headers); toast.success('Image deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const copy = (img) => {
    const url = img.url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${img.url}` : img.url;
    navigator.clipboard?.writeText(url).then(() => toast.success('Image link copied')).catch(() => toast.info(url));
  };

  const shown = filterImages(images || [], q, usage, source);

  return (
    <div className="space-y-6" data-testid="images-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-[#1E1622]">Images</h2>
        <button onClick={onAddLink} data-testid="images-add-link" className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border border-[#3B164C]/30 text-[#3B164C] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors">
          <Link2 size={16} /> Add by link
        </button>
      </div>
      <DropZone onFiles={upload} busy={busy} />
      {images?.length > 0 && <LibraryFilters {...{ q, setQ, usage, setUsage, source, setSource }} />}
      {images === null && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#3B164C]" /></div>}
      {images?.length === 0 && (
        <div className="bg-white rounded-2xl border border-[#E6DAC5] p-12 text-center" data-testid="images-empty">
          <ImageIcon size={48} className="mx-auto text-[#1E1622]/20 mb-4" /><p className="text-[#1E1622]/50">No images uploaded yet</p>
        </div>
      )}
      {images?.length > 0 && shown.length === 0 && <p data-testid="library-no-matches" className="text-center text-sm text-[#1E1622]/60 py-10">No images match your search.</p>}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="images-grid">
        {shown.map((img) => <LibraryTile key={img.id} img={img} selected={false} onPick={copy} onDelete={remove} />)}
      </div>
      {shown.length > 0 && <p className="text-xs text-[#1E1622]/50">Click an image to copy its link. Images in use on the site can’t be deleted.</p>}
    </div>
  );
};
